import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isoWeek from "dayjs/plugin/isoWeek";
import type { WatchlistEarningsEntry } from "../../../../hooks/useWatchlistEarnings";
import type { EarningsFeedItem } from "./types";

// isoWeek: Monday-to-Sunday weeks, as the Watchlist view groups them. utc:
// the upcoming date arrives as midnight UTC standing for a calendar date, so
// it is read as that date wherever the reader is.
dayjs.extend(utc);
dayjs.extend(isoWeek);

/** One company's report on one calendar date: "AAPL|2026-11-02". */
export const upcomingKey = (ticker: string, date: string): string =>
  `${ticker.trim().toUpperCase()}|${date}`;

/** The reports that already have their card in the feed, by ticker and report date. */
export const reportedKeys = (items: readonly EarningsFeedItem[]): Set<string> =>
  new Set(
    items.map((item) =>
      upcomingKey(item.company.symbol, item.report.reportDate),
    ),
  );

/** "Today", "Tomorrow", else "Mon 2 Nov". */
export const upcomingDayLabel = (day: string, now: number): string => {
  const today = dayjs(now);
  if (day === today.format("YYYY-MM-DD")) return "Today";
  if (day === today.add(1, "day").format("YYYY-MM-DD")) return "Tomorrow";
  return dayjs(day).format("ddd D MMM");
};

export interface UpcomingDay {
  day: string;
  label: string;
  entries: WatchlistEarningsEntry[];
}

/**
 * This week's reports, a group per day in date order: each ticker once
 * across lists, and none that already has its card (it has moved to
 * Reported). A date that couldn't be looked up, or isn't set, isn't this week.
 */
export const thisWeekDays = (
  entries: readonly WatchlistEarningsEntry[],
  reported: ReadonlySet<string>,
  now: number,
): UpcomingDay[] => {
  const today = dayjs(now);
  const start = today.startOf("isoWeek").format("YYYY-MM-DD");
  const end = today.endOf("isoWeek").format("YYYY-MM-DD");
  const seen = new Set<string>();
  const byDay = new Map<string, WatchlistEarningsEntry[]>();
  entries.forEach((entry) => {
    if (entry.earningsUnavailable || entry.nextEarningsDate === null) return;
    const date = dayjs.utc(entry.nextEarningsDate).format("YYYY-MM-DD");
    if (date < start || date > end) return;
    const ticker = entry.ticker.trim().toUpperCase();
    if (seen.has(ticker)) return;
    seen.add(ticker);
    if (reported.has(upcomingKey(ticker, date))) return;
    byDay.set(date, [...(byDay.get(date) ?? []), entry]);
  });
  return Array.from(byDay.keys())
    .sort()
    .map((day) => ({
      day,
      label: upcomingDayLabel(day, now),
      entries: byDay.get(day) ?? [],
    }));
};
